import React, { useEffect, useMemo, useState } from "react";
import TopBar from "../../components/TopBar";

/**
 * ManualePage.jsx
 *
 * API richieste:
 * - GET  /api/manuale_status.php
 * - POST /api/manuale_generate.php
 * - GET  /api/manuale_html.php
 * - POST /api/manuale_html_generate.php
 */

async function apiGet(url) {
  const r = await fetch(url, { credentials: "include" });
  const j = await r.json().catch(() => ({}));
  if (!r.ok || j?.ok === false) {
    throw new Error(j?.error || `Errore HTTP ${r.status}`);
  }
  return j;
}

async function apiPost(url, body = {}) {
  const r = await fetch(url, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  const j = await r.json().catch(() => ({}));
  if (!r.ok || j?.ok === false) {
    throw new Error(j?.error || `Errore HTTP ${r.status}`);
  }
  return j;
}

async function apiUpload(url, formData) {
  const r = await fetch(url, {
    method: "POST",
    credentials: "include",
    body: formData,
  });
  const j = await r.json().catch(() => ({}));
  if (!r.ok || j?.ok === false) {
    throw new Error(j?.error || `Errore HTTP ${r.status}`);
  }
  return j;
}

function Badge({ children, tone = "neutral" }) {
  const cls =
    tone === "ok"
      ? "border-emerald-200 bg-emerald-50 text-emerald-800"
      : tone === "warn"
        ? "border-amber-200 bg-amber-50 text-amber-800"
        : tone === "err"
          ? "border-red-200 bg-red-50 text-red-800"
          : "border-slate-200 bg-slate-50 text-slate-800";
  return (
    <span className={`inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-medium ${cls}`}>
      {children}
    </span>
  );
}

function Card({ title, right, children }) {
  return (
    <div className="rounded-2xl border bg-white shadow-sm">
      <div className="flex items-start justify-between gap-3 border-b p-4">
        <div className="text-base font-semibold">{title}</div>
        {right}
      </div>
      <div className="p-4">{children}</div>
    </div>
  );
}

function TabButton({ active, onClick, children }) {
  return (
    <button
      onClick={onClick}
      className={`rounded-xl px-3 py-2 text-sm font-medium ${active ? "bg-black text-white" : "border hover:bg-slate-50"
        }`}
    >
      {children}
    </button>
  );
}

/**
 * Costruisce l’indice accordion:
 * H2 = capitoli
 * H3 = sottotitoli
 */
function buildAccordionToc(tocFlat = []) {
  const chapters = [];
  let current = null;

  for (const item of tocFlat) {
    const level = Number(item.level || 0);
    if (level === 2) {
      current = { id: item.id, title: item.title, children: [] };
      chapters.push(current);
    } else if (level === 3) {
      if (!current) {
        current = { id: "generale", title: "Generale", children: [] };
        chapters.push(current);
      }
      current.children.push({ id: item.id, title: item.title });
    }
  }
  return chapters;
}

export default function ManualePage() {
  const [tab, setTab] = useState("pdf");
  const [err, setErr] = useState("");

  // PDF
  const [status, setStatus] = useState(null);
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [busyPdf, setBusyPdf] = useState(false);
  const [customManual, setCustomManual] = useState(null);
  const [customFile, setCustomFile] = useState(null);
  const [busyCustom, setBusyCustom] = useState(false);
  const [pdfBuster, setPdfBuster] = useState(() => Date.now()); // 🔥 cache bust

  // HTML
  const [htmlExists, setHtmlExists] = useState(false);
  const [htmlBody, setHtmlBody] = useState("");
  const [tocFlat, setTocFlat] = useState([]);
  const [loadingHtml, setLoadingHtml] = useState(false);
  const [busyHtml, setBusyHtml] = useState(false);

  const fileUrl = status?.file_url || null;
  const pdfExists = !!status?.exists;
  const pdfUpdated = status?.updated_at || "—";
  const rev = status?.rev || "—";

  const accordionToc = useMemo(() => buildAccordionToc(tocFlat), [tocFlat]);
  const [openChapterId, setOpenChapterId] = useState(null);

  async function loadStatus() {
    setErr("");
    setLoadingStatus(true);
    try {
      const j = await apiGet("/api/manuale_status.php");
      setStatus(j);
    } catch (e) {
      setErr(e.message || "Errore caricamento stato PDF");
    } finally {
      setLoadingStatus(false);
    }
  }

  async function loadCustomManual() {
    try {
      const j = await apiGet("/api/manuale_custom.php");
      setCustomManual(j);
    } catch (e) {
      setErr(e.message || "Errore caricamento manuale personalizzato");
    }
  }

  async function uploadCustomManual() {
    if (!customFile) return;
    setErr("");
    setBusyCustom(true);
    try {
      const formData = new FormData();
      formData.append("manuale", customFile);
      const j = await apiUpload("/api/manuale_custom.php", formData);
      setCustomManual(j);
      setCustomFile(null);
      const input = document.getElementById("custom-manual-file");
      if (input) input.value = "";
    } catch (e) {
      setErr(e.message || "Errore caricamento manuale personalizzato");
    } finally {
      setBusyCustom(false);
    }
  }

  async function loadHtml() {
    setErr("");
    setLoadingHtml(true);
    try {
      const j = await apiGet("/api/manuale_html.php");
      setHtmlExists(!!j.exists);
      setHtmlBody(j.html || "");
      setTocFlat(Array.isArray(j.toc) ? j.toc : []);
    } catch (e) {
      setErr(e.message || "Errore caricamento HTML");
    } finally {
      setLoadingHtml(false);
    }
  }

  async function generateHtml() {
    setErr("");
    setBusyHtml(true);
    try {
      await apiPost("/api/manuale_html_generate.php", {});
      await loadHtml();
      setTab("html");
    } catch (e) {
      setErr(e.message || "Errore generazione HTML");
    } finally {
      setBusyHtml(false);
    }
  }

  // 🔥 ORDINE CORRETTO: HTML → PDF → reload iframe
  async function generatePdf() {
    setErr("");
    setBusyPdf(true);
    try {
      await apiPost("/api/manuale_html_generate.php", {});
      await apiPost("/api/manuale_generate.php", {});
      await loadStatus();
      setPdfBuster(Date.now());
      setTab("pdf");
    } catch (e) {
      setErr(e.message || "Errore generazione PDF");
    } finally {
      setBusyPdf(false);
    }
  }

  function scrollToId(id) {
    const el = document.getElementById(id);
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  useEffect(() => {
    loadStatus();
    loadCustomManual();
  }, []);

  useEffect(() => {
    if (tab === "html" && htmlBody === "" && !loadingHtml) {
      loadHtml();
    }
  }, [tab]);

  useEffect(() => {
    if (!openChapterId && accordionToc.length) {
      setOpenChapterId(accordionToc[0].id);
    }
  }, [accordionToc]);

  return (
    <>
      <TopBar />

      <div className="mx-auto max-w-6xl space-y-6 p-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold">Manuale HACCP</h1>
            <p className="mt-1 text-sm text-slate-600">
              PDF ufficiale + versione HTML navigabile.
            </p>
          </div>

          <div className="flex items-center gap-2">
            <TabButton active={tab === "pdf"} onClick={() => setTab("pdf")}>PDF</TabButton>
            <TabButton active={tab === "html"} onClick={() => setTab("html")}>HTML</TabButton>
          </div>
        </div>

        {err && (
          <div className="rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-800">
            {err}
          </div>
        )}

        {tab === "pdf" && (
          <div className="space-y-6">
            <Card
              title="Il tuo manuale HACCP"
              right={customManual?.exists ? <Badge tone="ok">Caricato</Badge> : <Badge>Facoltativo</Badge>}
            >
              <div className="space-y-4 text-sm">
                <p className="text-slate-600">
                  Carica il manuale HACCP della tua attività in formato PDF. Il documento resta separato
                  dal manuale generato da NORMA ed è disponibile soltanto per la tua azienda.
                </p>

                {customManual?.exists && (
                  <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-3">
                    <div className="font-medium text-emerald-900">
                      {customManual.original_name || "Manuale personalizzato"}
                    </div>
                    <div className="mt-1 text-xs text-emerald-700">
                      Aggiornato: {customManual.updated_at || "—"}
                    </div>
                  </div>
                )}

                <div className="flex flex-col gap-3 md:flex-row md:items-center">
                  <input
                    id="custom-manual-file"
                    type="file"
                    accept="application/pdf,.pdf"
                    onChange={(event) => setCustomFile(event.target.files?.[0] || null)}
                    className="min-w-0 flex-1 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm"
                  />
                  <button
                    type="button"
                    onClick={uploadCustomManual}
                    disabled={!customFile || busyCustom}
                    className="rounded-xl bg-[#003B71] px-4 py-2 text-sm font-medium text-white hover:bg-[#002B5B] disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {busyCustom ? "Caricamento…" : customManual?.exists ? "Sostituisci PDF" : "Carica PDF"}
                  </button>
                  {customManual?.file_url && (
                    <a
                      href={customManual.file_url}
                      target="_blank"
                      rel="noreferrer"
                      className="rounded-xl border px-4 py-2 text-center text-sm font-medium hover:bg-slate-50"
                    >
                      Apri PDF
                    </a>
                  )}
                </div>
                <p className="text-xs text-slate-400">Solo PDF, dimensione massima 30 MB.</p>
              </div>
            </Card>

            <Card
              title="Stato PDF"
              right={
                loadingStatus ? (
                  <Badge>Caricamento…</Badge>
                ) : pdfExists ? (
                  <Badge tone="ok">Disponibile</Badge>
                ) : (
                  <Badge tone="warn">Non creato</Badge>
                )
              }
            >
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-600">Ultimo aggiornamento</span>
                  <span className="font-medium">{pdfUpdated}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Revisione</span>
                  <span className="font-medium">{rev}</span>
                </div>

                <div className="pt-2 flex gap-2">
                  <button
                    onClick={generatePdf}
                    disabled={busyPdf}
                    className="rounded-xl bg-black px-3 py-2 text-sm font-medium text-white hover:opacity-90"
                  >
                    {busyPdf ? "Generazione…" : "Genera / Aggiorna PDF"}
                  </button>

                  {fileUrl && (
                    <a
                      href={fileUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="rounded-xl border px-3 py-2 text-sm font-medium hover:bg-slate-50"
                    >
                      Scarica PDF
                    </a>
                  )}
                </div>
              </div>
            </Card>

            {fileUrl && (
              <Card title="Anteprima PDF">
                <div className="mb-3 md:hidden">
                  <a
                    href={`${fileUrl}${fileUrl.includes("?") ? "&" : "?"}v=${pdfBuster}`}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex w-full items-center justify-center rounded-xl bg-blue-600 px-4 py-3 text-sm font-semibold text-white"
                  >
                    Apri anteprima PDF
                  </a>
                  <p className="mt-2 text-xs text-slate-500">
                    Nell’app il documento si apre nel visualizzatore PDF integrato.
                  </p>
                </div>
                <iframe
                  title="Anteprima Manuale PDF"
                  src={`${fileUrl}${fileUrl.includes("?") ? "&" : "?"}v=${pdfBuster}`}
                  className="hidden h-[78vh] w-full rounded-xl border md:block"
                />
              </Card>
            )}
          </div>
        )}

        {tab === "html" && (
          <div className="space-y-6">
            <Card
              title="Manuale HTML navigabile"
              right={
                <button
                  onClick={generateHtml}
                  disabled={busyHtml}
                  className="rounded-xl border px-3 py-2 text-sm font-medium hover:bg-slate-50"
                >
                  {busyHtml ? "Rigenero…" : "Rigenera HTML"}
                </button>
              }
            >
              <div className="grid gap-4 md:grid-cols-[320px_1fr]">
                <div className="rounded-2xl border p-3">
                  {accordionToc.map((ch) => {
                    const open = openChapterId === ch.id;
                    return (
                      <div key={ch.id} className="mb-2 rounded-xl border">
                        <button
                          className="flex w-full justify-between px-3 py-2 text-sm font-semibold"
                          onClick={() => setOpenChapterId(open ? null : ch.id)}
                        >
                          {ch.title}
                          <span>{open ? "−" : "+"}</span>
                        </button>
                        {open && (
                          <div className="border-t px-2 py-2 space-y-1">
                            <button
                              onClick={() => scrollToId(ch.id)}
                              className="block w-full rounded px-2 py-1 text-left text-sm hover:bg-slate-50"
                            >
                              Vai al capitolo
                            </button>
                            {ch.children.map((s) => (
                              <button
                                key={s.id}
                                onClick={() => scrollToId(s.id)}
                                className="block w-full rounded px-2 py-1 text-left text-sm hover:bg-slate-50"
                              >
                                {s.title}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                <div className="rounded-2xl border p-4">
                  {loadingHtml ? (
                    <div className="text-sm text-slate-500">Caricamento…</div>
                  ) : htmlExists ? (
                    <div
                      className="prose max-w-none"
                      dangerouslySetInnerHTML={{ __html: htmlBody }}
                    />
                  ) : (
                    <div className="text-sm text-slate-600">
                      HTML non ancora generato.
                    </div>
                  )}
                </div>
              </div>
            </Card>
          </div>
        )}
      </div>
    </>
  );
}
